import React, { useState, useEffect } from 'react';
import { apiClient } from '../../apiClient';
import { writeAuditLog } from '../../lib/auditLogger';
import { Download, Search, RefreshCw } from 'lucide-react';

const AUDIT_ACTIONS = [
    { value: '', label: 'すべてのイベント' },
    { value: 'SYSTEM_LOGIN', label: 'SYSTEM_LOGIN (ログイン)' },
    { value: 'SYSTEM_LOGOUT', label: 'SYSTEM_LOGOUT (ログアウト)' },
    { value: 'COMPANY_VIEW', label: 'COMPANY_VIEW (企業詳細閲覧)' },
    { value: 'COMPANY_EXPORT', label: 'COMPANY_EXPORT (企業CSV出力)' },
    { value: 'USER_VIEW', label: 'USER_VIEW (ユーザー詳細閲覧)' },
    { value: 'USER_EXPORT', label: 'USER_EXPORT (ユーザーCSV出力)' },
    { value: 'MAIL_USER_EXPORT', label: 'MAIL_USER_EXPORT (メルマガユーザーCSV出力)' },
    { value: 'COMPANY_SUSPEND', label: 'COMPANY_SUSPEND (企業利用停止)' },
    { value: 'COMPANY_RESUME', label: 'COMPANY_RESUME (企業利用再開)' },
    { value: 'CSVJOB_VIEW', label: 'CSVJOB_VIEW (CSV処理履歴詳細閲覧)' },
    { value: 'INVOICE_VIEW', label: 'INVOICE_VIEW (請求書発行履歴詳細閲覧)' },
    { value: 'STRIPELOG_VIEW', label: 'STRIPELOG_VIEW (Stripe連携ログ詳細閲覧)' },
    { value: 'ACTIVITYLOG_VIEW', label: 'ACTIVITYLOG_VIEW (アクティビティログ詳細閲覧)' },
    { value: 'AUDITLOG_VIEW', label: 'AUDITLOG_VIEW (監査ログ閲覧)' },
    { value: 'AUDITLOG_EXPORT', label: 'AUDITLOG_EXPORT (監査ログCSV出力)' },
];

const AuditLog = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    // Search / Filter states
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [action, setAction] = useState('');
    const [actorId, setActorId] = useState('');
    const [resourceId, setResourceId] = useState('');

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            if (action) params.append('action', action);
            if (actorId) params.append('actorId', actorId);
            if (resourceId) params.append('resourceId', resourceId);

            const response = await apiClient.get(`/v1/admin/audit-logs?${params.toString()}`);
            if (response.ok) {
                const data = await response.json();
                setLogs(data);
            }
        } catch (error) {
            console.error('Error fetching audit logs:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
        // Record AUDITLOG_VIEW event when page opens
        writeAuditLog({
            action: 'AUDITLOG_VIEW',
            resourceType: 'audit_log',
            resourceId: '',
            metadata: { source: 'admin_console' }
        });
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        fetchLogs();
    };

    const handleReset = () => {
        setStartDate('');
        setEndDate('');
        setAction('');
        setActorId('');
        setResourceId('');
    };

    const handleExportCsv = async () => {
        setExporting(true);
        try {
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            if (action) params.append('action', action);
            if (actorId) params.append('actorId', actorId);
            if (resourceId) params.append('resourceId', resourceId);

            const response = await apiClient.get(`/v1/admin/audit-logs/export?${params.toString()}`);
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error('Error exporting audit logs:', error);
            alert('CSVエクスポートに失敗しました。');
        } finally {
            setExporting(false);
        }
    };

    const formatDate = (isoString) => {
        if (!isoString) return '-';
        const d = new Date(isoString);
        return d.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Audit Log (監査ログ)</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        システム管理者によるセキュリティ重要操作の監査記録（保存期間: 365日）
                    </p>
                </div>
                <button
                    onClick={handleExportCsv}
                    disabled={exporting}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                >
                    <Download size={18} />
                    {exporting ? '出力中...' : 'CSVエクスポート'}
                </button>
            </div>

            {/* Filter Section */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">開始日</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">終了日</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">イベント</label>
                        <select
                            value={action}
                            onChange={(e) => setAction(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500"
                        >
                            {AUDIT_ACTIONS.map(act => (
                                <option key={act.value} value={act.value}>{act.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">管理者 (UID / メール)</label>
                        <input
                            type="text"
                            placeholder="管理者UID"
                            value={actorId}
                            onChange={(e) => setActorId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">対象ID (Resource ID)</label>
                        <input
                            type="text"
                            placeholder="テナントID / ユーザーID"
                            value={resourceId}
                            onChange={(e) => setResourceId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500"
                        />
                    </div>

                    <div className="md:col-span-2 lg:col-span-5 flex justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={handleReset}
                            className="flex items-center gap-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-100 transition"
                        >
                            <RefreshCw size={16} /> クリア
                        </button>
                        <button
                            type="submit"
                            className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition"
                        >
                            <Search size={16} /> 検索
                        </button>
                    </div>
                </form>
            </div>

            {/* Audit Log Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-gray-700">
                        <thead className="bg-gray-100 text-gray-600 uppercase text-xs font-semibold border-b border-gray-200">
                            <tr>
                                <th className="px-4 py-3">日時</th>
                                <th className="px-4 py-3">管理者</th>
                                <th className="px-4 py-3">イベント</th>
                                <th className="px-4 py-3">対象種別</th>
                                <th className="px-4 py-3">対象ID</th>
                                <th className="px-4 py-3">結果</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-8 text-gray-500">
                                        読み込み中...
                                    </td>
                                </tr>
                            ) : logs.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-8 text-gray-500">
                                        監査ログが見つかりません。
                                    </td>
                                </tr>
                            ) : (
                                logs.map((log) => (
                                    <tr key={log.id} className="hover:bg-gray-50 transition">
                                        <td className="px-4 py-3 font-mono text-xs text-gray-600 whitespace-nowrap">
                                            {formatDate(log.timestamp)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-gray-900">{log.actorEmail || '-'}</div>
                                            <div className="text-xs text-gray-400 font-mono">{log.actorId}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="font-mono text-xs font-semibold px-2 py-1 bg-gray-100 text-gray-800 rounded">
                                                {log.action}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-xs font-medium text-gray-600 uppercase">
                                            {log.resourceType || '-'}
                                        </td>
                                        <td className="px-4 py-3 font-mono text-xs text-gray-600">
                                            {log.resourceId || '-'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                                                log.result === 'success'
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-red-100 text-red-800'
                                            }`}>
                                                {log.result === 'success' ? 'success' : 'failure'}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AuditLog;
